// src/pages/artist/ContractManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import contractService from '../../services/contractService';
import { useToast } from '../../hooks/useToast';
import { extractApiList } from '../../utils/responseAdapters';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract, faCircleCheck, faClock, faBan,
  faTriangleExclamation, faRotateLeft, faXmark,
} from '@fortawesome/free-solid-svg-icons';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#f59e0b', icon: faClock            },
  active:     { label: 'Active',     color: '#10b981', icon: faCircleCheck      },
  terminated: { label: 'Terminated', color: '#ef4444', icon: faBan              },
  expired:    { label: 'Expired',    color: '#6b7280', icon: faBan              },
  rejected:   { label: 'Rejected',   color: '#ef4444', icon: faXmark            },
  cancelled:  { label: 'Cancelled',  color: '#6b7280', icon: faXmark            },
};

function daysRemaining(endDate) {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#6b7280', icon: faClock };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: meta.color + '22', color: meta.color,
      padding: '3px 10px', borderRadius: 20, fontWeight: 600, fontSize: 13,
    }}>
      <FontAwesomeIcon icon={meta.icon} size="xs" />
      {meta.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Request Form ─────────────────────────────────────────────────────────────

function RequestContractForm({ managers, onSubmit, loading, preselectedManagerId }) {
  const [adminId, setAdminId] = useState(preselectedManagerId || '');
  const [duration, setDuration] = useState(6);
  const [termsAgreed, setTermsAgreed] = useState(false);

  useEffect(() => {
    if (preselectedManagerId) setAdminId(preselectedManagerId);
  }, [preselectedManagerId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAgreed) return;
    onSubmit(adminId, duration, termsAgreed);
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 480 }}>
      <div className="form-group">
        <label>Select Manager</label>
        <select
          value={adminId}
          onChange={e => setAdminId(e.target.value)}
          required
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc' }}
        >
          <option value="">— Choose a manager —</option>
          {managers.map(m => (
            <option key={m.client_id} value={m.client_id}>
              {m.name} ({m.email}) · {m.managed_artist} artist{m.managed_artist !== 1 ? 's' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Contract Duration</label>
        <select
          value={duration}
          onChange={e => setDuration(Number(e.target.value))}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc' }}
        >
          {[3, 6, 12, 24].map(m => (
            <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
          ))}
        </select>
      </div>

      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <input
          type="checkbox"
          id="terms"
          checked={termsAgreed}
          onChange={e => setTermsAgreed(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <label htmlFor="terms" style={{ cursor: 'pointer', fontSize: 13, color: '#555' }}>
          I agree that the selected manager may manage my artwork tokenization applications for the contract period. I understand that the contract is artist-level and covers all artworks I submit for tokenization.
        </label>
      </div>

      <Button type="submit" variant="tertiary" disabled={!termsAgreed || !adminId || loading}>
        <FontAwesomeIcon icon={faFileContract} style={{ marginRight: 6 }} />
        {loading ? 'Submitting…' : 'Send Contract Request'}
      </Button>
    </form>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ArtistContractManagement() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmTerminate, setConfirmTerminate] = useState(null);
  const [preselectedManager, setPreselectedManager] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'request' | 'history'

  const activeContract = contracts.find(c => c.status === 'active');
  const pendingContract = contracts.find(c => c.status === 'pending');
  const historyContracts = contracts.filter(c => !['active', 'pending'].includes(c.status));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contractsRes, managersRes] = await Promise.all([
        contractService.getMyContractsArtist(),
        contractService.getAvailableManagers(),
      ]);
      const cData = extractApiList(contractsRes?.data);
      const mData = extractApiList(managersRes?.data);
      setContracts(Array.isArray(cData) ? cData : []);
      setManagers(Array.isArray(mData) ? mData : []);
    } catch {
      showToast('Failed to load contract data', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Default tab: if active contract → show it; if pending → show it; else → request
  useEffect(() => {
    if (!loading) {
      if (activeContract) setActiveTab('active');
      else if (pendingContract) setActiveTab('pending');
      else setActiveTab('request');
    }
  }, [loading, activeContract, pendingContract]);

  const handleRequest = async (adminId, durationMonths, termsAgreed) => {
    setSubmitting(true);
    try {
      await contractService.requestContract(adminId, durationMonths, termsAgreed);
      showToast('Contract request sent! The manager will review it in their dashboard.', 201);
      await fetchData();
    } catch {
      showToast('Failed to send contract request', 500);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (contractId) => {
    try {
      await contractService.cancelContractRequest(contractId);
      showToast('Contract request withdrawn', 200);
      await fetchData();
    } catch {
      showToast('Failed to withdraw request', 500);
    }
  };

  const handleTerminate = async (contractId) => {
    try {
      await contractService.terminateContractArtist(contractId);
      showToast('Contract terminated', 200);
      setConfirmTerminate(null);
      await fetchData();
    } catch {
      showToast('Failed to terminate contract', 500);
    }
  };

  const handleRenew = (managerId) => {
    setPreselectedManager(managerId);
    setActiveTab('request');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading contracts…</div>
      </DashboardLayout>
    );
  }

  const days = activeContract ? daysRemaining(activeContract.end_date) : null;
  const expiringSOon = days !== null && days <= 30;

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px', maxWidth: 800 }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <FontAwesomeIcon icon={faFileContract} style={{ fontSize: 22, color: ' #ffffff' }} />
          <h2 style={{ margin: 0, color: '#ee8326' }}>Management Contracts</h2>
        </div>

        {/* Tab nav */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #eee' }}>
          {[
            { key: 'active',  label: 'Active Contract' },
            { key: 'pending', label: `Pending${pendingContract ? ' (1)' : ''}` },
            { key: 'request', label: 'Request Contract' },
            { key: 'history', label: `History (${historyContracts.length})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '8px 16px', border: 'none', background: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #dd4014' : '2px solid transparent',
                color: activeTab === tab.key ? '#f0de78' : '#555',
                fontWeight: activeTab === tab.key ? 700 : 400,
                cursor: 'pointer', marginBottom: -2,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Active tab ── */}
        {activeTab === 'active' && (
          <div>
            {activeContract ? (
              <div className="application-card" style={{ position: 'relative' }}>
                {expiringSOon && (
                  <div style={{
                    background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
                    padding: '10px 16px', marginBottom: 16, display: 'flex',
                    alignItems: 'center', gap: 10, color: '#92400e', fontSize: 14,
                  }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Contract expires in <strong>{days} day{days !== 1 ? 's' : ''}</strong> — consider renewing early.
                    <button
                      onClick={() => handleRenew(activeContract.admin_id)}
                      style={{ marginLeft: 'auto', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Renew Early
                    </button>
                  </div>
                )}
                <div className="card-header">
                  <h3 style={{ margin: 0 }}>
                    Managed by{' '}
                    <span style={{ color: '#2d8cf0' }}>
                      {activeContract.manager_info?.name || activeContract.admin_id}
                    </span>
                  </h3>
                  <StatusBadge status="active" />
                </div>
                <p style={{ color: '#555', margin: '4px 0 16px' }}>
                  {activeContract.manager_info?.email}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  {[
                    ['Contract ID', activeContract.contract_id],
                    ['Duration', `${activeContract.duration_months} month${activeContract.duration_months !== 1 ? 's' : ''}`],
                    ['Start Date', formatDate(activeContract.start_date)],
                    ['End Date', formatDate(activeContract.end_date)],
                    ['Days Remaining', days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                      <div style={{ fontWeight: 600 }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <Button
                    variant="tertiary"
                    onClick={() => setConfirmTerminate(activeContract.contract_id)}
                  >
                    Terminate Contract
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                <FontAwesomeIcon icon={faFileContract} style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }} />
                <p>No active contract. You need a manager to apply for tokenization.</p>
                <Button variant="secondary" onClick={() => setActiveTab('request')}>
                  Request a Manager
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Pending tab ── */}
        {activeTab === 'pending' && (
          <div>
            {pendingContract ? (
              <div className="application-card">
                <div className="card-header">
                  <h3 style={{ margin: 0 }}>
                    Awaiting approval from{' '}
                    <span style={{ color: '#f59e0b' }}>
                      {pendingContract.manager_info?.name || pendingContract.admin_id}
                    </span>
                  </h3>
                  <StatusBadge status="pending" />
                </div>
                <p style={{ color: '#555', margin: '4px 0 16px' }}>
                  {pendingContract.manager_info?.email}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  {[
                    ['Contract ID', pendingContract.contract_id],
                    ['Duration Requested', `${pendingContract.duration_months} month${pendingContract.duration_months !== 1 ? 's' : ''}`],
                    ['Requested On', formatDate(pendingContract.created_at)],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                      <div style={{ fontWeight: 600 }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <Button variant="tertiary" onClick={() => handleCancel(pendingContract.contract_id)}>
                    <FontAwesomeIcon icon={faRotateLeft} style={{ marginRight: 6 }} />
                    Withdraw Request
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                <p>No pending contract requests.</p>
                <Button variant="secondary" onClick={() => setActiveTab('request')}>
                  Request a Manager
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Request tab ── */}
        {activeTab === 'request' && (
          <div>
            {(activeContract || pendingContract) ? (
              <div style={{ padding: '20px', background: '#fef3c7', borderRadius: 10, color: '#92400e', marginBottom: 20 }}>
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />
                You already have a <strong>{activeContract ? 'active' : 'pending'}</strong> contract.
                Only one contract can be active or pending at a time.
              </div>
            ) : (
              <>
                <h3 style={{ marginBottom: 16, color: '#1f7496' }}>Request a Management Contract</h3>
                <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
                  A management contract gives a manager oversight of your artwork tokenization applications for the contract duration. You will not be able to apply for TitTat tokenization without an active contract.
                </p>
                <RequestContractForm
                  managers={managers}
                  onSubmit={handleRequest}
                  loading={submitting}
                  preselectedManagerId={preselectedManager}
                />
              </>
            )}
          </div>
        )}

        {/* ── History tab ── */}
        {activeTab === 'history' && (
          <div>
            {historyContracts.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>No past contracts.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {historyContracts.map(c => (
                  <div key={c.contract_id} className="application-card">
                    <div className="card-header">
                      <div>
                        <strong>{c.manager_info?.name || c.admin_id}</strong>
                        <div style={{ fontSize: 13, color: '#888' }}>{c.manager_info?.email}</div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, color: '#555' }}>
                      <span>Duration: <strong>{c.duration_months}mo</strong></span>
                      <span>From: <strong>{formatDate(c.start_date)}</strong></span>
                      <span>To: <strong>{formatDate(c.end_date)}</strong></span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#aaa' }}>
                      ID: {c.contract_id}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Terminate Confirm Dialog ── */}
      {confirmTerminate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Terminate Contract?</h3>
            <p style={{ color: '#555' }}>
              Terminating the contract will remove your manager's oversight. You will not be able to submit new tokenization applications until you establish a new contract. Existing pending applications will remain visible to the manager.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="tertiary" onClick={() => setConfirmTerminate(null)}>Cancel</Button>
              <Button variant="secondary" onClick={() => handleTerminate(confirmTerminate)}>
                Yes, Terminate
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
